import React from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { formatCurrency, formatPercent } from '@/utils/formatters';
import type { Store } from '@/types/store';

const STATUS_COLORS = {
  active: '#00E676',
  paused: '#FFB300',
  warning: '#FF6D00',
  error: '#FF1744',
};

const STATUS_LABELS = {
  active: 'ACTIF',
  paused: 'PAUSE',
  warning: 'ALERTE',
  error: 'ERREUR',
};

const PLATFORM_ICONS = {
  shopify: '🛍',
  etsy: '🧶',
  amazon: '📦',
};

const StoreCard: React.FC<{ store: Store; agents: ReturnType<typeof useSimulationStore.getState>['agents'] }> = ({ store, agents }) => {
  const assignedAgents = agents.filter(a => store.assignedAgentIds.includes(a.id));
  const statusColor = STATUS_COLORS[store.status];

  return (
    <div
      className="rounded-lg border bg-bg-elevated p-4 flex flex-col gap-3"
      style={{ borderColor: store.color + '40', boxShadow: `0 0 20px ${store.color}10` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">{store.emoji}</span>
            <div>
              <div className="font-pixel text-sm" style={{ color: store.color }}>{store.name}</div>
              <div className="font-mono text-xs text-gray-500">{store.niche}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className="font-mono text-xs px-2 py-0.5 rounded"
            style={{ color: statusColor, background: statusColor + '20', border: `1px solid ${statusColor}40` }}
          >
            {STATUS_LABELS[store.status]}
          </span>
          <span className="font-mono text-xs text-gray-600">
            {PLATFORM_ICONS[store.platform]} {store.platform}
          </span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2">
        <KPI label="CA du jour" value={formatCurrency(store.metrics.revenue)} color="#FFD700" growth={store.metrics.growth} />
        <KPI label="Commandes" value={String(store.metrics.orders)} color="#00E5FF" />
        <KPI label="Sessions" value={store.metrics.sessions.toLocaleString('fr')} color="#E040FB" />
        <KPI label="Conversion" value={formatPercent(store.metrics.conversionRate, 2)} color="#00E676" />
        <KPI label="Marge" value={formatPercent(store.metrics.margin)} color="#7C4DFF" />
        <KPI label="Panier moy." value={formatCurrency(store.metrics.avgOrderValue)} color="#FFB300" />
      </div>

      {/* Agents assignés */}
      <div>
        <div className="font-mono text-xs text-gray-600 mb-1">AGENTS ASSIGNÉS</div>
        <div className="flex gap-1 flex-wrap">
          {assignedAgents.map(agent => (
            <span
              key={agent.id}
              className="font-pixel text-xs px-1.5 py-0.5 rounded"
              style={{ background: agent.color + '20', color: agent.color, border: `1px solid ${agent.color}40`, fontSize: '6px' }}
            >
              {agent.codename}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const KPI: React.FC<{ label: string; value: string; color: string; growth?: number }> = ({ label, value, color, growth }) => (
  <div className="bg-bg-panel rounded p-2 border border-bg-border">
    <div className="font-mono text-xs text-gray-600 mb-0.5">{label}</div>
    <div className="flex items-end gap-1">
      <span className="font-mono font-bold" style={{ color }}>{value}</span>
      {growth !== undefined && (
        <span className={`font-mono text-xs mb-0.5 ${growth >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
          {growth >= 0 ? '▲' : '▼'}{Math.abs(growth).toFixed(1)}%
        </span>
      )}
    </div>
  </div>
);

export const StoresView: React.FC = () => {
  const stores = useSimulationStore(s => s.stores);
  const agents = useSimulationStore(s => s.agents);

  const totalRevenue = stores.reduce((s, st) => s + st.metrics.revenue, 0);
  const totalOrders = stores.reduce((s, st) => s + st.metrics.orders, 0);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Résumé global */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-pixel text-sm text-gray-200 mb-1">BOUTIQUES POD</h1>
          <div className="font-mono text-xs text-gray-500">{stores.length} boutiques · {totalOrders} commandes · {formatCurrency(totalRevenue)} CA</div>
        </div>
        <button className="px-4 py-2 rounded font-mono text-xs text-neon-cyan border border-neon-cyan/40 hover:bg-neon-cyan/10 transition-colors">
          + Nouvelle boutique
        </button>
      </div>

      {/* Grille */}
      <div className="grid grid-cols-2 gap-4">
        {stores.map(store => (
          <StoreCard key={store.id} store={store} agents={agents} />
        ))}
      </div>
    </div>
  );
};
